<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <search v-model="search"
                :placeholder="'user.type_the_client_code_to_search' | trans"
                v-on:enter="onSearchEnter"/>

        <table class="table table-sm" v-if="!isMobile">
          <thead class="thead-light">
          <tr>
            <th>{{ 'user.clients' | trans }}</th>
            <th>{{ 'user.number' | trans }}</th>
            <th>{{ 'protocol.completed_protocols' | trans }}</th>
            <th>{{ 'protocol.active_protocols' | trans }}</th>
            <th>{{ 'bids.bids_for_new_protocols' | trans }}</th>
            <th v-if="canUpdateClient && canUpdateTherapist && !isMensurationForbidden" class="action-cell"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="loading">
            <td colspan="6">
              <loader class="table-loader" messages="home.labels.loading_message_table"/>
            </td>
          </tr>
          <tr v-for="item in items" :key="item.id" v-if="!loading">
            <td class="wide-col">
              <router-link
                  :to="{ name: 'client-edit', params: { clientId: item.id }}">
                <div class="icon-circle icon-purple"><span v-if="!isMobile">{{'user.C' | trans}}</span></div>

                <strong>{{ item.code }}</strong>
              </router-link>
            </td>
            <td class="nowrap">{{ item.hash }}</td>
            <td class="text-center">{{ item.completedAssignedProtocolsCount }}</td>
            <td class="text-center">{{ item.activeAssignedProtocolsCount }}</td>
            <td class="text-center">{{ item.newBidsCount }}</td>
            <td v-if="!isMensurationForbidden">
              <a href="" class="delete-icon"
                 v-if="canUpdateClient && canUpdateTherapist"
                 @click.prevent="onDeleteItemIconClick(item)">
                <mdc-icon icon="delete"></mdc-icon>
              </a>
            </td>
          </tr>
          </tbody>
        </table>

        <div class="mobile-table" v-else>
          <div class="mobile-table__body">
            <div class="d-flex" v-if="loading">
              <loader class="table-loader" messages="home.labels.loading_message_table"></loader>
            </div>

            <div class="mobile-table__row" v-for="item in items" :key="item.id" v-if="!loading">
              <div class="title d-flex align-items-center justify-content-between">
                <router-link
                    class="d-flex align-items-center"
                    :to="{ name: 'client-edit', params: { clientId: item.id }}">
                  <div class="icon-circle icon-purple"></div>

                  <strong>{{ item.code }}</strong>
                </router-link>

                <div v-if="!isMensurationForbidden">
                  <a href="" class="delete-icon"
                     v-if="canUpdateClient && canUpdateTherapist"
                     @click.prevent="onDeleteItemIconClick(item)">
                    <mdc-icon icon="delete"></mdc-icon>
                  </a>
                </div>
              </div>

              <div class="description pt-2">
                <div>
                  <p class="gray-dark">{{ 'user.number' | trans }}</p>
                  <p>{{ item.hash }}</p>
                </div>

                <div>
                  <p class="gray-dark">{{ 'protocol.completed_protocols' | trans }}</p>
                  <p>{{ item.completedAssignedProtocolsCount }}</p>
                </div>

                <div>
                  <p class="gray-dark">{{ 'protocol.active_protocols' | trans }}</p>
                  <p>{{ item.activeAssignedProtocolsCount }}</p>
                </div>

                <div>
                  <p class="gray-dark">{{ 'bids.bids_for_new_protocols' | trans }}</p>
                  <p>{{ item.newBidsCount }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2 mt-md-0">
      <div class="col-md-12 col-lg-5">
        <nav class="nav justify-content-center justify-content-md-start" v-show="paginate.lastPage > 1">
          <paginate :page-count="paginate.lastPage"
                    :page-range="paginate.pageRange"
                    :margin-pages="paginate.marginPages"
                    :initial-page="paginate.page"
                    :force-page="paginate.forcePage"
                    :click-handler="onPaginationClick"
                    :container-class="'pagination pagination-sm'"
                    :page-class="'page-item'"
                    :page-link-class="'page-link'">
            <span slot="prevContent" class="page-link">&laquo;</span>
            <span slot="nextContent" class="page-link">&raquo;</span>
          </paginate>
        </nav>
      </div>

      <div class="col-md-12 col-lg-7 text-right" v-if="!isMensurationForbidden">
        <mdc-button class="button-primary" raised v-if="canUpdateClient && canUpdateTherapist" v-on:click="onAttachClientButtonClick">
          {{ 'user.add_client' | trans }}
        </mdc-button>
      </div>
    </div>

    <confirm-delete-modal v-bind:visible="modals.confirmDelete.isVisible"
                          v-bind:item="modals.confirmDelete.client"
                          v-on:accept="onAcceptDeleteClient"
                          v-on:cancel="onCancelDeleteClient"/>

    <attach-client-modal v-bind:visible="modals.attachClient.isVisible"
                         v-bind:therapist-id="therapistId"
                         v-on:accept="onAcceptAttachClient"
                         v-on:cancel="onCancelAttachClient"/>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import TherapistApiManager from '../../api/therapist'
  import Loader from '../../../loader/components/Loader'
  import {pagination} from '../../../../common/mixins/pagination'
  import ConfirmDeleteModal from './ConfirmDeleteModal'
  import AttachClientModal from './AttachClientModal'
  import Search from '../../../../common/table/Search'

  export default {
    components: {Search, AttachClientModal, ConfirmDeleteModal, Loader},
    mixins: [pagination],

    name: 'Clients',
    props: {
      therapistId: {
        required: true,
        type: [Number, String],
      },
    },
    data () {
      return {
        items: [],
        search: null,
        sort: '-id',
        state: {
          created: false,
          loadingCount: 0,
        },
        modals: {
          confirmDelete: {
            client: null,
            isVisible: false,
          },
          attachClient: {
            isVisible: false,
          },
        },
      }
    },
    async created () {
      this.state.created = false
      await this.fetchData()
      this.state.created = true
    },
    computed: {
      ...mapGetters('mobile', [
        'isMobile',
      ]),
      ...mapGetters('auth', [
        'canUpdateTherapist',
        'canUpdateClient',
        'isMensurationForbidden',
      ]),
      loading: {
        get: function () {
          return this.state.loadingCount > 0
        },
        set: function (newVal) {
          if (newVal === false) {
            this.state.loadingCount--
            return
          }
          this.state.loadingCount++
        },
      },
    },
    methods: {
      setLoading: function (state) {
        this.loading = state
        this.$emit('loading', state, this.state.created)
      },
      toggleConfirmDeleteModal: function () {
        this.modals.confirmDelete.isVisible = !this.modals.confirmDelete.isVisible
      },
      toggleAttachClientModal: function () {
        this.modals.attachClient.isVisible = !this.modals.attachClient.isVisible
      },
      fetchData: async function () {
        try {
          this.setLoading(true)

          let response = await TherapistApiManager.paginateAllClientsByTherapistId({
            therapistId: this.therapistId,
            perPage: this.paginate.perPage,
            page: this.paginate.page,
            sort: this.sort,
            search: this.search,
          })

          let {items, perPage, lastPage, currentPage} = response.data.data
          this.items = items
          this.setPaginate({perPage, lastPage, currentPage})
        } catch (e) {
          console.error(e)
        } finally {
          this.setLoading(false)
        }
      },
      deleteClient: async function (client) {
        try {
          this.setLoading(true)

          await TherapistApiManager.deleteClient(this.therapistId, client.id)
          await this.fetchData()
        } catch (e) {
          console.error(e)
        } finally {
          this.setLoading(false)
        }
      },
      addClients: async function (clientIds) {
        try {
          this.setLoading(true)

          await TherapistApiManager.addClients({therapistId: this.therapistId, clientIds})
          await this.fetchData()
        } catch (e) {
          console.error(e)
        } finally {
          this.setLoading(false)
        }
      },
      onDeleteItemIconClick: function (item) {
        this.toggleConfirmDeleteModal()
        this.modals.confirmDelete.client = item
      },
      onPaginationClick: function (pageNum) {
        this.onPaginationBtnClick(pageNum)
        this.fetchData()
      },
      onAcceptDeleteClient: function (item) {
        this.toggleConfirmDeleteModal()

        if (((this.items.length - 1) === 0) && ((this.paginate.page - 1) !== 0)) {
          this.paginate.page -= 1
          this.paginate.forcePage -= 1
        }

        this.deleteClient(item)
        this.modals.confirmDelete.client = null
      },
      onCancelDeleteClient: function (item) {
        this.toggleConfirmDeleteModal()
        this.modals.confirmDelete.client = null
      },
      onAttachClientButtonClick: function () {
        this.toggleAttachClientModal()
      },
      onAcceptAttachClient: function (itemId) {
        this.toggleAttachClientModal()
        this.addClients([itemId])
      },
      onCancelAttachClient: function () {
        this.toggleAttachClientModal()
      },
      onSearchEnter: function (text) {
        this.search = text
        this.fetchData()
      },
    },
  }
</script>



// WEBPACK FOOTER //
// src/modules/user/therapist/profile/Clients.vue